<template>
  <page-wrapper ref="pageEl" title="รายงาน Earn Coin">
    <div ref="page-container" class="page-container row">

      <!-- Loading Section -->
      <LoadingOverlay :active="isLoading" />

      <!-- Report Section -->
      <div class="report-filter col-12">
        <div class="form">

          <div class="section">
            <div>
              <div class="label">วันที่ได้รับ MNC</div>
              <date-picker v-model="receiveMNCDateRange" type="daterange"></date-picker>
            </div>
            <div>
              <div class="label">โครงการ</div>
              <project-select
                v-model="selectedProject"
                :selected-project-code="selectedProject"
              >
              </project-select>
            </div>
          </div>

          <div class="section">
            <div>
              <div class="label">กิจกรรมที่ได้รับ MNC</div>
              <el-select
                v-model="selectedMncEvent"
                filterable
                style="width: 100%"
              >
                <el-option v-for="(t, idx) in filterMncEvent" :key="idx" :label="t.label" :value="t.value"></el-option>
              </el-select>
            </div>
            <div>
              <div class="label">Transaction Status</div>
              <el-select
                v-model="selectedTransactionStatus"
                filterable
                style="width: 100%"
              >
                <el-option
                  v-for="(t, idx) in filterTransactionStatus"
                  :key="idx"
                  :label="t.label"
                  :value="t.value"
                ></el-option>
              </el-select>
            </div>
          </div>

          <div style="display: flex; justify-content: center;">

            <el-button
              type="primary"
              @click="handleSearch"
            >
              ค้นหา
            </el-button>

            <el-button
              type="info"
              @click.prevent="downloadReport"
            >
              ดาวน์โหลด
            </el-button>

            <el-button
              plain
              type="danger"
              @click="handleClickClear"
            >
              รีเซ็ต
            </el-button>

          </div>
        </div>
      </div>

      <div class="report-content col-12">
        <div>
          <div v-show="latestUpdateTime" class="report-header">
            <div>
              <b>รายงานอัปเดต</b>
              <p>
                {{ latestUpdateMessage() }}
              </p>
            </div>
            <div>
              <div>
                <p>
                  {{ totalCount(total, limit, reports.length, page) }}
                </p>
              </div>
              <div v-if="total !== 0" align="right">
                <el-button
                  v-for="i in [10, 20, 30]"
                  :key="i"
                  :type="limit === i ? 'primary' : ''"
                  circle
                  size="small"
                  @click="limit = i"
                >
                  {{ i }}
                </el-button>
              </div>
            </div>
          </div>

          <div class="mt-3">
            <DataTable :data="reports" :headers="headers" :loading="isLoading">
            </DataTable>
          </div>

          <div v-if="total > 0" class="mt-4" style="text-align: center;">
            <el-pagination
              :total="total"
              :current-page.sync="page"
              :page-size="limit"
              background
              layout="prev, pager, next"
              @current-change="handleCurrentChange"
            >
            </el-pagination>
          </div>
        </div>

      </div>
    </div>
  </page-wrapper>
</template>

<script>
import { DateTime } from "luxon"
import { backendAdminClient } from "@/dependencies"
import { toLocaleTH, validateDateRangeLimit } from "@/lib/dateTime"

// Components
import LoadingOverlay from '@/lib/components/LoadingOverlay'
import PageWrapper from "@/lib/components/PageWrapper"
import DatePicker from "@/lib/components/DatePicker"
import ProjectSelect from "@/lib/components/ProjectSelect"
import DataTable from "@/lib/components/DataTable"

// MNC Presenters
import { buildEarnCoinHeaders } from "../presenters/earnCoinHeaders"
import { transformEarnReport } from "../presenters/earnCoinTransformer"
import { BASE_MNC_EVENT_OPTIONS } from "../utils/earnCoinEventOptions"

export default {
  components: {
    PageWrapper,
    DatePicker,
    ProjectSelect,
    DataTable,
    LoadingOverlay,
  },
  data() {
    return {
      filterMncEvent: [],
      filterTransactionStatus: [
        { label: "ทั้งหมด", value: undefined },
        { label: "Success", value: "SUCCESS" },
        { label: "Pending", value: "PENDING" },
        { label: "Failed", value: "FAILED" }
      ],
      receiveMNCDateRange: [],
      selectedProject: undefined,
      selectedMncEvent: undefined,
      selectedTransactionStatus: undefined,
      page: 1,
      limit: 10,
      total: 0,
      latestUpdateTime: null,
      isLoading: true,
      reports: [],
      headers: buildEarnCoinHeaders(this.dateFormat.bind(this)),
    }
  },
  computed: {
    queryParams() {
      const {
        selectedProject: projectCode,
        selectedMncEvent: mncEvent,
        selectedTransactionStatus: transactionStatus,
        receiveMNCDateRange,
        page,
        limit
      } = this.$route.query
      return {
        mncEvent,
        transactionStatus,
        mncReceivedDate: this.convertDateRange(receiveMNCDateRange),
        projectCode,
        page: (page && +page) || 1,
        limit: +limit || this.limit
      }
    }
  },
  watch: {
    "$route.query": async function watchRouteQuery() {
      this.fetchReports()
      const pageContent = this.$refs.pageEl?.$el?.querySelector(".page-content")
      if (pageContent) pageContent.scrollTop = 0
    },
    async limit() {
      await this.handleSearch()
    }
  },
  created() {
    this.fetchSeq = 0
    this.loadingCount = 0
  },
  mounted() {
    this.fetchReports()
    this.getMNCEventFilterList()
  },
  methods: {
    syncFormFromRoute() {
      const {
        selectedProject,
        selectedMncEvent,
        selectedTransactionStatus,
        receiveMNCDateRange,
        page,
        limit
      } = this.$route.query
      this.selectedProject = selectedProject || null
      this.selectedMncEvent = selectedMncEvent || null
      this.selectedTransactionStatus = selectedTransactionStatus || null
      this.receiveMNCDateRange = this.setupQueryDateRange(receiveMNCDateRange)
      this.page = (page && +page) || 1
      this.limit = +limit || this.limit
    },
    handleSearch() {
      const projectCode = this.selectedProject?.projectCode ?? this.selectedProject ?? null
      const {
        selectedMncEvent,
        selectedTransactionStatus,
        receiveMNCDateRange,
        limit
      } = this
      let query = {
        selectedProject: projectCode,
        selectedMncEvent,
        selectedTransactionStatus,
        receiveMNCDateRange: this.convertDateRange(receiveMNCDateRange),
        limit,
        page: 1
      }
      // NOTE: filter out null value
      query = Object.fromEntries(
        Object.entries(query).filter(([, value]) => value != null)
      )
      // NOTE: replace route with query string
      const route = {
        name: this.$route.name,
        query
      }
      this.$router.replace(route)
    },
    setupQueryDateRange(dateRange) {
      if (dateRange && dateRange.length > 1) {
        const [from, to] = dateRange
        return [DateTime.fromISO(from), DateTime.fromISO(to)]
      }
      return []
    },
    convertDateRange(dateRange) {
      if (!dateRange || dateRange.length <= 1) return undefined
      const parsed = dateRange.map((date) => (
        DateTime.isDateTime(date) ? date : DateTime.fromISO(date)
      ))
      if (parsed.some((dt) => !dt.isValid)) return undefined
      return parsed.map((dt) => dt.toISO())
    },
    handleClickClear() {
      // $route.query watcher will trigger fetchReports after the replace.
      const route = {
        name: this.$route.name
      }
      this.$router.replace(route)
    },
    handleCurrentChange(page) {
      const route = {
        name: this.$route.name,
        query: { ...this.$route.query, page: (page && +page) || 1 }
      }
      this.$router.replace(route)
    },
    async fetchReports() {

      // Sync form fields from URL
      this.syncFormFromRoute()

      // Loading
      this.beginLoading()
      this.fetchSeq += 1
      const seq = this.fetchSeq

      try {

        // Request Reports
        const query = this.queryParams
        const { data } = await backendAdminClient.report.getEarnReport(query)

        // Stale-response guard
        if (seq !== this.fetchSeq) return

        const { result } = data

        // Set State
        this.reports = this.transformReport(result.reports)
        this.latestUpdateTime = result.timestamp

        // Pagination
        this.total = result.pagination?.total_records ?? 0
      }
      catch (error) {
        if (seq !== this.fetchSeq) return
        this.reports = []
        this.total = 0
        this.latestUpdateTime = null
        this.$message.error('ไม่สามารถโหลดข้อมูลรายงานได้')
      }
      finally {
        // Always pair endLoading with the beginLoading above (counter must stay balanced).
        this.endLoading()
      }
    },
    totalCount(total, limit, reportsLength, page) {
      if (!total) return "ไม่พบข้อมูล"

      const currentPage = +page || 1
      const isLastPage = limit > reportsLength

      const end = isLastPage
        ? total : currentPage * limit
      const start = isLastPage
        ? limit * (currentPage - 1) + 1
        : (currentPage - 1) * limit + 1

      return `แสดงผล ${start.toLocaleString()} - ${end.toLocaleString()} จากทั้งหมด ${total.toLocaleString()}`
    },
    dateFormat(date, format = "dd/MM/yyyy") {
      const dt = DateTime.isDateTime(date)
        ? date
        : DateTime.fromISO(date)
      return toLocaleTH(dt).toFormat(format)
    },
    transformReport(reports) {
      return transformEarnReport(reports)
    },
    async downloadReport() {

      // Get Query
      const query = this.queryParams

      // Validate Report Result
      if (this.reports.length === 0) {
        this.$alert(
          'ไม่พบข้อมูลที่ต้องการดาวน์โหลด',
          'แจ้งเตือน', {
            confirmButtonText: 'ปิด', type: 'warning'
          })
        return
      }

      // Validate Daterange
      const { mncReceivedDate } = query

      if (!mncReceivedDate || mncReceivedDate.length === 0) {
        this.$alert(
          'กรุณาระบุวันที่ได้รับ MNC และระบุช่วงเวลาได้ไม่เกิน 1 เดือน',
          'แจ้งเตือน', {
            confirmButtonText: 'ปิด',
            type: 'warning'
          })
        return
      }

      // Validate Range Limit
      const isLimit = validateDateRangeLimit(
        mncReceivedDate[0], mncReceivedDate[1]
      )

      // Range Limit Validator
      if (isLimit) {
        this.$alert(
          'ระบุช่วงเวลาได้ไม่เกิน 1 เดือน',
          'แจ้งเตือน', {
            confirmButtonText: 'ปิด',
            type: 'warning'
          })
        return
      }

      // Warning Download Message — user-cancellable; cancel rejects $confirm.
      try {
        await this.$confirm(
          'การดาวน์โหลดจะใช้เวลาดำเนินการสักครู่ กรุณาอย่าออกจากหน้าจอ หรือรีเฟรชหน้า',
          'แจ้งเตือน', {
            confirmButtonText: 'ดาวน์โหลด',
            cancelButtonText: 'ปิด',
            type: 'info'
          }
        )
      }
      catch (cancelled) {
        return
      }

      // Set Loading
      this.beginLoading()

      try {

        // Download Query (copy — don't mutate the cached computed `queryParams`)
        const downloadQuery = { ...query, download: true }

        // Send Request
        const response = await backendAdminClient.report.getEarnReport(downloadQuery)

        // Get Date
        const dateFilename = this.dateFormat(
          DateTime.utc(), 'yyyy-MM-dd-hmmss'
        )

        // Transform Data
        const blob = new Blob([response.data])

        // Create DOM Element
        const link = document.createElement("a")
        link.href = window.URL.createObjectURL(blob)
        link.download = `${dateFilename}-mnc-earn-reports.xlsx`

        // Download File
        link.click()

        // Clear Element
        link.remove()
      }
      catch (error) {
        this.$message.error('ดาวน์โหลดรายงานไม่สำเร็จ')
      }
      finally {
        // Set Un-Loading
        this.endLoading()
      }
    },
    latestUpdateMessage() {
      const format = "dd MMMM yyyy เวลา HH:mm น."
      return this.dateFormat(this.latestUpdateTime, format)
    },
    async getMNCEventFilterList() {

      try {

        // Set Loading
        this.beginLoading()

        // Get unique data
        const { data } = await backendAdminClient.report.getMncEvents()

        // Mapping Data
        const mappingList = (data.result ?? []).map((item) => ({
          label: item.mnc_event_name,
          value: item.mnc_event
        }))

        // Set Filters (always keep BASE options even when API returns empty)
        this.filterMncEvent = [
          ...BASE_MNC_EVENT_OPTIONS,
          ...mappingList
        ]
      }
      catch (error) {
        this.$message.error('โหลดรายการกิจกรรมไม่สำเร็จ')
      }
      finally {
        this.endLoading()
      }
    },
    beginLoading() {
      this.loadingCount += 1
      this.isLoading = true
    },
    endLoading() {
      this.loadingCount = Math.max(0, this.loadingCount - 1)
      if (this.loadingCount === 0) this.isLoading = false
    },
  }
}
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.header {
  z-index: 1;
}

.page-container {
  padding: 15px;
}

.report-cell {
  a {
    text-decoration: underline;
  }

  padding: 5px;
  border-left: 1px solid rgb(206, 204, 204);
  border-bottom: 1px solid rgb(228, 225, 225);

  &:empty::before {
    content: "N/A";
    color: #d8d8d8;
  }
}

.page-container {
  padding: 1rem;
  margin: 0;
}

.report-filter {
  width: 100%;
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.report-filter .form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form .section {
  display: grid;
  gap: 1rem;
  margin-top: 15px;
  grid-template-columns: repeat(2, 1fr);
}

.form .section[col="3"] {
  grid-template-columns: repeat(3, 1fr);
}

.label {
  font-size: .9rem;
  margin-bottom: .25rem;
}

.report-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.report-content .report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.el-button.el-button--danger.is-plain:hover,
.el-button.el-button--danger.is-plain:focus {
  color: white !important;
}

@media screen and (max-width: 640px) {

  .form .section,
  .form .section[col="3"] {
    grid-template-columns: 1fr;
  }

  .report-filter {
    margin-bottom: 1rem;
  }
}

.el-input__inner {
  text-align: center;
}

.el-date-editor--daterange,
.el-input__inner {
  width: 100%;
}
</style>
